<script setup lang="ts">
const emits = defineEmits<{
  clicked: [];
}>();

defineProps({
  imageSource: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  icon: {
    type: String,
    required: true,
  },
});
</script>

<template>
  <div class="card-wrapper">
    <div
      class="card"
      @click="emits('clicked')"
    >
      <span class="card__title">
        {{ title }}
        <i :class="`pi ${icon}`" />
      </span>
      <div class="card__image-wrapper">
        <img
          class="card__image"
          :src="imageSource"
        />
        <div class="card__gradient" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 2rem;
  padding: 0;
  width: 100%;

  @media (max-width: $media-max-tablet) {
    gap: 1rem;
    height: 100%;
  }
}

.card {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  width: 30rem;
  min-height: 30rem;
  cursor: pointer;
  overflow: hidden;
  border-radius: 1.5rem;
  border: 8px solid $white;
  box-shadow:
    0 4px 6px rgba($black-100, 0.1),
    0 10px 15px rgba($black-100, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: $media-max-tablet) {
    width: 100%;
    min-height: 30rem;
    border-width: 4px;
    border-radius: 1rem;
  }

  @media (max-width: $media-max-phone) {
    min-height: 25rem;
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow:
      0 8px 12px rgba($black-100, 0.15),
      0 15px 20px rgba($black-100, 0.1);

    @media (max-width: $media-max-tablet) {
      transform: translateY(-3px);
    }
  }

  &__title {
    color: $white;
    font-weight: 800;
    font-size: 2.5rem;
    z-index: 10;
    margin-bottom: 2rem;
    pointer-events: none;
    text-shadow: 0 2px 4px rgba($black-100, 0.3);
    transition: all 0.3s ease;
    text-align: center;
    padding: 0 1rem;

    @media (max-width: $media-max-tablet) {
      font-size: 2rem;
      margin-bottom: 1.5rem;
    }

    @media (max-width: $media-max-phone) {
      font-size: 1.75rem;
      margin-bottom: 1rem;
    }

    i {
      margin-left: 0.5rem;
      font-size: 2rem;

      @media (max-width: $media-max-tablet) {
        font-size: 1.75rem;
      }

      @media (max-width: $media-max-phone) {
        font-size: 1.5rem;
      }
    }
  }

  &__image-wrapper {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  &__image {
    object-fit: cover;
    width: 100%;
    height: 100%;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      transform: scale(1.1);

      @media (max-width: $media-max-tablet) {
        transform: scale(1.05);
      }
    }
  }

  &__gradient {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(
      to top,
      rgba($black-100, 0.8) 0%,
      rgba($black-100, 0.6) 30%,
      rgba($black-100, 0.2) 70%,
      transparent 100%
    );
  }
}
</style>
